/**
 * MediaSidebar — sidebar for the Media portal.
 * Six flat navigation items, logout.
 */

import { Link, useLocation } from 'react-router-dom'
import { X, TrendingUp, Search, BookOpen, Bell, BarChart2, Settings, LogOut } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useAuth } from '@/contexts/AuthContext'

interface MediaSidebarProps {
  isOpen: boolean
  onClose: () => void
}

interface NavItem {
  label: string
  path: string
  icon: React.ElementType
  exact?: boolean
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Trend & Notizie', path: '/media', icon: TrendingUp, exact: true },
  { label: 'Cerca bandi', path: '/media/bandi', icon: Search },
  { label: 'Letteratura ARC', path: '/media/letteratura', icon: BookOpen },
  { label: 'Alert settore', path: '/media/alerts', icon: Bell },
  { label: 'Report & Statistiche', path: '/media/reports', icon: BarChart2 },
  { label: 'Impostazioni', path: '/profile', icon: Settings, exact: true },
]

export function MediaSidebar({ isOpen, onClose }: MediaSidebarProps) {
  const location = useLocation()
  const { logout } = useAuth()

  const isActive = (item: NavItem) => {
    if (item.exact) return location.pathname === item.path
    return location.pathname === item.path || location.pathname.startsWith(item.path + '/')
  }

  const navLinkClass = (item: NavItem) =>
    cn(
      'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
      isActive(item)
        ? 'bg-primary text-primary-foreground'
        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
    )

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={cn(
          'fixed top-0 left-0 z-50 h-screen w-64 border-r bg-background transition-transform lg:translate-x-0 lg:static',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-full flex-col">
          <div className="flex h-16 items-center justify-between border-b px-4 lg:hidden">
            <span className="font-semibold">Menu</span>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          <div className="hidden h-16 items-center border-b px-4 lg:flex">
            <Link to="/media" className="flex items-center gap-2" onClick={onClose}>
              <span className="text-lg font-bold tracking-tight text-primary">
                Research<span className="text-foreground">Constellation</span>
              </span>
            </Link>
          </div>

          <nav className="flex-1 space-y-1 overflow-y-auto p-4">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={navLinkClass(item)}
                  onClick={onClose}
                >
                  <Icon className="h-5 w-5 shrink-0" />
                  <span>{item.label}</span>
                </Link>
              )
            })}
          </nav>

          <div className="border-t p-4">
            <button
              onClick={() => {
                onClose()
                logout()
              }}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10"
            >
              <LogOut className="h-5 w-5 shrink-0" />
              <span>Esci</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  )
}
